import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useIsMobile from "../hooks/useIsMobile";
import useSheetData from "../hooks/useSheetData";
import Reveal from "../components/common/Reveal";
import SectionTag from "../components/common/SectionTag";
import Icon from "../components/common/Icon";
import { SheetLoading } from "../components/common/SheetState";
import Seo from "../components/common/Seo";
import { SERVICES } from "../data/constants";
import { colors, fonts, emphasis, btnPrimary, btnOutline, btnOutlineOnDark, btnGhost, sectionPad, maxW, springHover, cardLift, rowShift, iconPop, arrowSlide } from "../styles/theme";

const CAPABILITIES = [
  { icon: "Globe", label: "Web Development" },
  { icon: "Cloud", label: "Cloud Solutions" },
  { icon: "Smartphone", label: "Mobile Apps" },
  { icon: "PenTool", label: "UI/UX Design" },
];

export default function HomePage() {
  const mob = useIsMobile();
  const { data: STATS, status: statsStatus } = useSheetData("Stats");

  return (
    <div>
      <Seo
        title="Charith — Web, Cloud, Mobile & Design Agency"
        description="Charith is a full-service digital agency delivering web, cloud, mobile and design solutions, and training the next generation of tech talent through hands-on internships."
        path="/"
      />
      {/* HERO */}
      <section style={{ background: colors.paper, paddingTop: mob ? 130 : 168, paddingBottom: mob ? 56 : 88 }}>
        <div style={{ ...maxW, padding: "0 6%" }}>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "7fr 5fr", gap: mob ? 44 : 60, alignItems: "start" }}>
            <div>
              <SectionTag>Digital Agency</SectionTag>
              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.1 }}
                style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 40 : "clamp(42px,5.4vw,74px)", lineHeight: 1.06, color: colors.ink, marginBottom: 26, letterSpacing: "-.01em" }}
              >
                Engineering tomorrow's <span style={emphasis}>digital world</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
                style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: mob ? 16 : 18, lineHeight: 1.7, marginBottom: 36, maxWidth: 480 }}
              >
                Charith is a full-service digital agency delivering world-class web, cloud, mobile and design solutions — while training the next generation of tech talent.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.32 }} style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <motion.div whileHover={{ backgroundColor: colors.rust, borderColor: colors.rust }}>
                  <Link to="/services" style={{ ...btnPrimary, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    Explore Services <Icon name="ArrowRight" size={16} />
                  </Link>
                </motion.div>
                <motion.div whileHover={{ backgroundColor: colors.ink, color: colors.cream }}>
                  <Link to="/internship" style={{ ...btnOutline, textDecoration: "none", display: "inline-block" }}>Join Internship</Link>
                </motion.div>
              </motion.div>
            </div>

            <Reveal delay={0.15}>
              <motion.div
                initial="rest" whileHover="hover" variants={cardLift} transition={springHover}
                style={{ border: `1px solid ${colors.borderStrong}`, borderRadius: 6, padding: mob ? 26 : 32, background: colors.paperAlt }}
              >
                <p style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: colors.inkFaint, marginBottom: 20 }}>Capabilities</p>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {CAPABILITIES.map((c, i) => (
                    <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0", borderTop: i === 0 ? "none" : `1px solid ${colors.border}` }}>
                      <motion.div variants={iconPop} transition={springHover} style={{ display: "flex", flexShrink: 0 }}>
                        <Icon name={c.icon} size={20} style={{ color: colors.rust }} />
                      </motion.div>
                      <span style={{ fontFamily: fonts.body, fontWeight: 500, fontSize: 15, color: colors.ink }}>{c.label}</span>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 22, paddingTop: 22, borderTop: `1px solid ${colors.borderStrong}`, fontFamily: fonts.display, fontStyle: "italic", fontSize: 16, color: colors.inkDim, lineHeight: 1.6 }}>
                  "Now accepting interns for the 2026 program."
                </p>
              </motion.div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      {statsStatus === "loading" ? (
        <div style={{ background: colors.paper, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, textAlign: "center" }}>
          <SheetLoading label="Loading stats…" />
        </div>
      ) : STATS.length > 0 ? (
        <div style={{ background: colors.paper, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, ...sectionPad(mob), paddingTop: mob ? 32 : 48, paddingBottom: mob ? 32 : 48 }}>
          <div style={{ ...maxW, display: "grid", gridTemplateColumns: mob ? "1fr 1fr" : "repeat(4,1fr)" }}>
            {STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.06} style={{ textAlign: "center", padding: "0 12px", borderLeft: (!mob && i > 0) ? `1px solid ${colors.border}` : "none" }}>
                <div style={{ fontFamily: fonts.display, fontStyle: "italic", fontWeight: 500, fontSize: mob ? 32 : 44, color: colors.ink, lineHeight: 1 }}>{s.n}</div>
                <div style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 12.5, marginTop: 8, textTransform: "uppercase", letterSpacing: ".08em" }}>{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      ) : null}

      {/* SERVICES PREVIEW */}
      <section style={{ background: colors.paper, ...sectionPad(mob) }}>
        <div style={{ ...maxW }}>
          <Reveal style={{ marginBottom: mob ? 36 : 56, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
            <div>
              <SectionTag>What We Do</SectionTag>
              <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 30 : "clamp(28px,3.4vw,46px)", color: colors.ink, letterSpacing: "-.01em" }}>Our Services</h2>
            </div>
            <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 14.5, maxWidth: 340, lineHeight: 1.7 }}>End-to-end digital solutions engineered to elevate your business.</p>
          </Reveal>
          <div style={{ borderTop: `1px solid ${colors.borderStrong}` }}>
            {SERVICES.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <motion.div
                  initial="rest" whileHover="hover" variants={rowShift} transition={springHover}
                  style={{ display: "grid", gridTemplateColumns: mob ? "auto 1fr" : "60px 220px 1fr auto", alignItems: mob ? "start" : "center", gap: mob ? 16 : 28, padding: mob ? "22px 4px" : "30px 20px", borderBottom: `1px solid ${colors.border}` }}
                >
                  <span style={{ fontFamily: fonts.display, fontStyle: "italic", fontSize: 20, color: colors.inkFainter, gridRow: mob ? "1 / 3" : "auto" }}>{String(i + 1).padStart(2, "0")}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <motion.div variants={iconPop} transition={springHover} style={{ display: "flex" }}>
                      <Icon name={s.icon} size={20} style={{ color: s.color, flexShrink: 0 }} />
                    </motion.div>
                    <h3 style={{ fontFamily: fonts.body, fontWeight: 700, fontSize: 16, color: colors.ink }}>{s.title}</h3>
                  </div>
                  <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 14, lineHeight: 1.65, gridColumn: mob ? "2 / 3" : "auto", maxWidth: 460 }}>{s.desc}</p>
                  {!mob && (
                    <Link to="/services" style={{ ...btnGhost, color: colors.ink, fontFamily: fonts.body, fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      Learn more <motion.span variants={arrowSlide} transition={springHover} style={{ display: "flex" }}><Icon name="ArrowUpRight" size={14} /></motion.span>
                    </Link>
                  )}
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: colors.ink, ...sectionPad(mob) }}>
        <Reveal style={{ ...maxW, textAlign: mob ? "left" : "center" }}>
          <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 28 : "clamp(26px,3.6vw,48px)", color: colors.cream, letterSpacing: "-.01em", marginBottom: 20, lineHeight: 1.15 }}>
            Ready to build something <span style={{ ...emphasis, color: colors.mustard }}>great</span>?
          </h2>
          <p style={{ color: colors.creamDim, fontFamily: fonts.body, fontSize: mob ? 15 : 17, lineHeight: 1.75, marginBottom: 36, maxWidth: 560, margin: mob ? "0 0 36px" : "0 auto 36px" }}>
            Whether you're a business going digital or a student launching your career — Charith is your partner in growth.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: mob ? "flex-start" : "center", flexWrap: "wrap" }}>
            <motion.div whileHover={{ backgroundColor: colors.rust, borderColor: colors.rust }}>
              <Link to="/contact" style={{ background: colors.cream, border: `1.5px solid ${colors.cream}`, color: colors.ink, fontFamily: fonts.body, fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 3, textDecoration: "none", display: "inline-block" }}>Start a Project</Link>
            </motion.div>
            <motion.div whileHover={{ backgroundColor: colors.cream, color: colors.ink }}>
              <Link to="/internship" style={{ ...btnOutlineOnDark, textDecoration: "none", display: "inline-block" }}>Apply for Internship</Link>
            </motion.div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
